/**
 * FilterPopover - 筛选条件弹框组件
 * 用于设置国家/地区和粉丝数量范围筛选条件
 */

import React, { useState } from 'react';
import { Input } from 'antd';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Globe, Users } from 'lucide-react';
import { useTranslation } from 'next-i18next';

interface FilterPopoverProps {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
  country?: string;
  onCountryChange?: (country: string) => void;
  followerRange?: [number, number];
  onFollowerRangeChange?: (range: [number, number]) => void;
}

// 粉丝数量范围标签
const followerRangeLabels = ['0', '1K', '10K', '50K', '100K', '500K', '1M', '5M+'];

export const FilterPopover: React.FC<FilterPopoverProps> = ({
  open,
  onOpenChange,
  trigger,
  country: externalCountry,
  onCountryChange,
  followerRange: externalFollowerRange,
  onFollowerRangeChange,
}) => {
  const [internalCountry, setInternalCountry] = useState('');
  const [internalFollowerRange, setInternalFollowerRange] = useState<[number, number]>([
    0,
    followerRangeLabels.length - 1,
  ]);

  const { t } = useTranslation();

  const country = externalCountry !== undefined ? externalCountry : internalCountry;
  const followerRange = externalFollowerRange !== undefined ? externalFollowerRange : internalFollowerRange;

  const handleCountryChange = (value: string) => {
    if (onCountryChange) {
      onCountryChange(value);
    } else {
      setInternalCountry(value);
    }
  };

  const handleFollowerRangeChange = (range: [number, number]) => {
    // 确保小值在前，大值在后
    const sortedRange: [number, number] = [Math.min(range[0], range[1]), Math.max(range[0], range[1])];

    if (onFollowerRangeChange) {
      onFollowerRangeChange(sortedRange);
    } else {
      setInternalFollowerRange(sortedRange);
    }
  };

  // 计算滑块位置百分比
  const getSliderPosition = (index: number) => {
    return (index / (followerRangeLabels.length - 1)) * 100;
  };

  const startPercent = getSliderPosition(followerRange[0]);
  const endPercent = getSliderPosition(followerRange[1]);

  // 计算实际的开始和结束值（支持交叉）
  const minValue = Math.min(followerRange[0], followerRange[1]);
  const maxValue = Math.max(followerRange[0], followerRange[1]);
  const minPercent = getSliderPosition(minValue);
  const maxPercent = getSliderPosition(maxValue);

  return (
    <Popover open={open} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent className="w-80 p-[18px]" align="start" side="bottom">
        <div className="flex flex-col gap-4">
          {/* 国家/地区选择 */}
          <div className="flex flex-col gap-3">
            <label className="flex items-center gap-2 font-medium text-sm leading-[14px] tracking-[-0.1504px] text-neutral-950">
              <Globe className="w-4 h-4" />
              {t('Enter a country or region')}
            </label>
            <Input
              value={country}
              onChange={(e) => handleCountryChange(e.target.value)}
              placeholder={t('Please enter ...')}
              className="h-9 rounded-lg border-[rgba(0,0,0,0.15)] text-sm placeholder:opacity-30 placeholder:text-[#0c0c0c]"
            />
          </div>

          {/* 粉丝数量范围 */}
          <div className="flex flex-col gap-3">
            <label className="flex items-center gap-2 font-medium text-sm leading-[14px] tracking-[-0.1504px] text-neutral-950">
              <Users className="w-4 h-4" />
              {t('Follower Count')}
            </label>

            {/* 自定义滑块 */}
            <div className="relative pt-2">
              {/* 滑块轨道容器 */}
              <div className="relative px-2 py-2">
                {/* 背景轨道 */}
                <div className="relative h-4 w-full">
                  {/* 灰色背景 */}
                  <div className="absolute inset-y-0 left-0 right-0 bg-gray-200 rounded-full" />

                  {/* 紫色激活区域 */}
                  <div
                    className="absolute inset-y-0 bg-[#725cff] rounded-full"
                    style={{
                      left: `calc(${minPercent}%)`,
                      right: `calc(${100 - maxPercent}%)`,
                    }}
                  />

                  {/* 第一个滑块 */}
                  <div
                    className="absolute top-1/2 w-4 h-4 -mt-2 bg-white border border-[#725cff] rounded-full cursor-pointer transition-transform hover:scale-110 z-10 touch-none"
                    style={{
                      left: `${startPercent}%`,
                    }}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      const target = e.currentTarget;
                      target.setPointerCapture(e.pointerId);

                      const container = target.parentElement;
                      if (!container) return;

                      const rect = container.getBoundingClientRect();
                      const width = rect.width;
                      let rafId: number | null = null;

                      const handlePointerMove = (moveEvent: PointerEvent) => {
                        if (rafId !== null) return;

                        rafId = requestAnimationFrame(() => {
                          const x = moveEvent.clientX - rect.left;
                          const percent = Math.max(0, Math.min(100, (x / width) * 100));
                          const index = Math.round((percent / 100) * (followerRangeLabels.length - 1));
                          handleFollowerRangeChange([index, followerRange[1]]);
                          rafId = null;
                        });
                      };

                      const handlePointerUp = () => {
                        if (rafId !== null) {
                          cancelAnimationFrame(rafId);
                          rafId = null;
                        }
                        target.removeEventListener('pointermove', handlePointerMove);
                        target.removeEventListener('pointerup', handlePointerUp);
                      };

                      target.addEventListener('pointermove', handlePointerMove);
                      target.addEventListener('pointerup', handlePointerUp);
                    }}
                  />

                  {/* 第二个滑块 */}
                  <div
                    className="absolute top-1/2 w-4 h-4 -mt-2 bg-white border border-[#725cff] rounded-full cursor-pointer transition-transform hover:scale-110 z-10 touch-none"
                    style={{
                      left: `calc(${endPercent}% - 16px)`,
                    }}
                    onPointerDown={(e) => {
                      e.preventDefault();
                      const target = e.currentTarget;
                      target.setPointerCapture(e.pointerId);

                      const container = target.parentElement;
                      if (!container) return;

                      const rect = container.getBoundingClientRect();
                      const width = rect.width;
                      let rafId: number | null = null;

                      const handlePointerMove = (moveEvent: PointerEvent) => {
                        if (rafId !== null) return;

                        rafId = requestAnimationFrame(() => {
                          const x = moveEvent.clientX - rect.left;
                          const percent = Math.max(0, Math.min(100, (x / width) * 100));
                          const index = Math.round((percent / 100) * (followerRangeLabels.length - 1));
                          handleFollowerRangeChange([followerRange[0], index]);
                          rafId = null;
                        });
                      };

                      const handlePointerUp = () => {
                        if (rafId !== null) {
                          cancelAnimationFrame(rafId);
                          rafId = null;
                        }
                        target.removeEventListener('pointermove', handlePointerMove);
                        target.removeEventListener('pointerup', handlePointerUp);
                      };

                      target.addEventListener('pointermove', handlePointerMove);
                      target.addEventListener('pointerup', handlePointerUp);
                    }}
                  />
                </div>
              </div>

              {/* 范围标签 */}
              <div className="flex items-center justify-between px-2 mt-2">
                <span className="flex-1 text-base leading-6 tracking-[-0.3125px] text-[#717182]">
                  {followerRangeLabels[minValue]}
                </span>
                <span className="text-base leading-6 tracking-[-0.3125px] text-[#717182]">-</span>
                <span className="flex-1 text-base leading-6 tracking-[-0.3125px] text-[#717182] text-end">
                  {followerRangeLabels[maxValue]}
                </span>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export { followerRangeLabels };
